/**
 * src/main.tsx
 * -------------
 * Application entry point for the Helix Customer Chat frontend.
 *
 * Fully implemented this will:
 *   - Mount the React 18 root into the #root DOM node
 *   - Wrap the tree with QueryClientProvider (@tanstack/react-query)
 *   - Initialize i18next before render (src/lib/i18n.ts)
 *   - Bootstrap the appearance store so CSS variables are applied
 *     before the first paint (prevents flash of unstyled content)
 *   - Set up Sentry error boundary if VITE_SENTRY_DSN is present
 *
 * Key dependencies:
 *   react, react-dom/client, @tanstack/react-query,
 *   src/App.tsx, src/lib/i18n.ts, src/stores/appearance.ts
 */
import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { initI18n } from './lib/i18n'
import './index.css'
import './styles/tokens.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
    },
  },
})

async function bootstrap() {
  // Initialize i18next before rendering so strings are ready
  await initI18n()

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

bootstrap()
